import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { forkJoin, from, Observable, of, Subject, Subscription, combineLatest } from 'rxjs';
import {catchError, finalize, mergeMap, switchMap, take, takeUntil, map, distinctUntilChanged} from 'rxjs/operators';
import {NgbModal, NgbModalOptions, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';
import { isEqual } from 'lodash-es'; // Potrebbe servire lodash per confronti profondi affidabili
import {Ambiguity, AmbiguityAgentResult, AnalysisContent, LinkedKnowledgeBaseItem} from '../../../../shared/templateGen/templateInterfaces';
import { StatoSessioneService } from '../../../../shared/services/stato-sessione.service';
import { AuthService } from '../../../../shared/auth/auth.service';
import { UserStoryService } from '../../../../shared/templateGen/template-api.service';
import {SessionPersistenceService} from '../../session-manager/session-persistence.service';
import {KnowledgeBaseItem} from '../../../../shared/services/persistence-api.service';


@Component({
  selector: 'app-ambiguities-alert',
  templateUrl: './ambiguities-alert.component.html',
  styleUrls: ['./ambiguities-alert.component.scss']
})
export class AmbiguitiesAlertComponent implements OnInit, OnDestroy {
  @ViewChild('ambiguitiesManagerModal') ambiguitiesManagerModal: any;
  @ViewChild('filteredAmbiguitiesModal') filteredAmbiguitiesModal: any;
  @ViewChild('ambiguityDetailsModal') ambiguityDetailsModal: any;
  @ViewChild('knowledgeBaseModalSelector') knowledgeBaseModalSelector: any;
  @ViewChild('agentDebugModal') agentDebugModal: TemplateRef<any>;
  @ViewChild('agentSelectionModal') agentSelectionModal: TemplateRef<any>;

  // RBAC
  isAdmin$: Observable<boolean>;

  // Agent debug state
  activeAgentDebug: any = null;
  agentDebugCopied = false;

  // Agent KB selection: which ambiguities to process
  selectedForAgent = new Set<string>();

  // Agent KB search progress
  agentSearching = false;
  agentProgress = { current: 0, total: 0 };
  private agentSearchSubscription: Subscription | null = null;

  private destroy$ = new Subject<void>();
  ambiguities: Ambiguity[] = [];
  filteredAmbiguities: Ambiguity[] = [];
  selectedAmbiguity: Ambiguity;
  hasAmbiguities = false;
  private subscriptions: Subscription = new Subscription();
  private isProcessing = false;

  // Knowledge base items
  knowledgeBaseItems: KnowledgeBaseItem[] = [];
  filteredKnowledgeBaseItems: KnowledgeBaseItem[] = [];
  selectedKnowledgeBaseItem: KnowledgeBaseItem | null = null;
  isLoadingKnowledgeBase = false;
  knowledgeBaseError: string | null = null;
  searchTerm = '';
  knowledgeBaseLoaded = false; // Flag to track if KB items were loaded
  private isNavigatingInternally = false;

  // Filter state
  currentFilter = {
    state: 'unanswered' as 'unanswered' | 'answered' | 'solved',
    priority: 'high' as 'high' | 'medium' | 'low'
  };

  filterTitle = '';
// Memorizza una copia dell'ambiguità originale prima di modificarla nei dettagli
  private originalSelectedAmbiguity: Ambiguity | null = null;
// NUOVA proprietà: Set per memorizzare gli ID delle ambiguità controllate in questa sessione
  private checkedAmbiguityIds = new Set<string>();
// Property to store the state of the ambiguity when the details modal was opened
  public initialSelectedAmbiguityStateForReviewButton: 'unanswered' | 'answered' | 'solved' | null = null;


  // Riferimenti ai modali aperti per gestirli meglio
  private managerModalRef: NgbModalRef | null = null;
  private filteredModalRef: NgbModalRef | null = null;
  private detailsModalRef: NgbModalRef | null = null;
  private kbSelectorModalRef: NgbModalRef | null = null;

  constructor(
    private modalService: NgbModal,
    private sessioneService: StatoSessioneService,
    private authService: AuthService,
    private userStoryService: UserStoryService,
    private sessionPersistenceService: SessionPersistenceService
  ) { }

  ngOnInit(): void {
    this.isAdmin$ = this.authService.hasRole('Admin');
    this.loadKnowledgeBaseItems(); // Load KB items once

    this.subscriptions.add(
      this.sessioneService.ambiguities$.pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)), // Keep for performance
        catchError(error => {
          console.error('Error in ambiguities$ subscription:', error);
          this.checkedAmbiguityIds.clear();
          this.ambiguities = [];
          this.hasAmbiguities = false;
          this.refreshFilteredListIfOpen();
          Swal.fire('Error', 'Failed to load ambiguities. Please check the console.', 'error');
          return []; // Return empty array to keep the stream alive
        })
      ).subscribe(ambiguitiesFromService => {
        const testAmbiguityId = 'YOUR_TEST_AMBIGUITY_ID'; // <-- REMEMBER TO REPLACE

        // console.log(`[ngOnInit sub REVISED] Received ${ambiguitiesFromService.length} ambiguities from service.`);
        const previousAmbiguityStateMap = new Map(this.ambiguities.map(a => [a.id, a.stato])); // For logging/comparison if needed

        // Process ambiguities from the service:
        // - Take 'stato' as truth from the service.
        // - Derive 'type' if it's a UI-only property.
        const newProcessedAmbiguities = ambiguitiesFromService.map(ambiguity => {
          // Log state of the test ambiguity as received from service
          if (ambiguity.id === testAmbiguityId) {
            // console.log(`[ngOnInit sub REVISED MAP ${testAmbiguityId}] From Service: id=${ambiguity.id}, stato=${ambiguity.stato}, resolution="${ambiguity.resolution}", linkedItems=${ambiguity.linkedKbItems?.length || 0}`);
          }
          return {
            ...ambiguity, // Spread all properties from the service object
            type: ambiguity.type || this.determineAmbiguityType(ambiguity.id), // Use LLM type or derive from ID
            priority: ambiguity.priority || 'medium', // Default priority for old ambiguities
          };
        });

        // Update the component's main list with the processed data from the service
        this.ambiguities = newProcessedAmbiguities;
        this.hasAmbiguities = this.ambiguities.length > 0;

        // Log the state of the test ambiguity after this.ambiguities has been updated
        const ambigForTestIdAfterProcessing = this.ambiguities.find(a => a.id === testAmbiguityId);
        // console.log(`[ngOnInit sub REVISED POST-ASSIGN] ${testAmbiguityId} in this.ambiguities:`, ambigForTestIdAfterProcessing ? JSON.parse(JSON.stringify(ambigForTestIdAfterProcessing)) : 'NOT FOUND');

        // Cleanup checkedAmbiguityIds based on the new state in this.ambiguities (which reflects service truth)
        const currentAmbiguityIdToStateMap = new Map(this.ambiguities.map(a => [a.id, a.stato]));
        this.checkedAmbiguityIds.forEach(checkedId => {
          const currentStatusInService = currentAmbiguityIdToStateMap.get(checkedId);
          if (!currentStatusInService || currentStatusInService !== 'answered') {
            // If ambiguity no longer exists or is no longer 'answered' according to the service
            this.checkedAmbiguityIds.delete(checkedId);
            // console.log(`[ngOnInit sub REVISED CHECKED CLEANUP] Ambiguity ${checkedId} removed from checkedSet (service status: ${currentStatusInService || 'REMOVED'})`);
          }
        });

        // Cleanup selectedForAgent: remove IDs of ambiguities that are no longer unanswered
        this.selectedForAgent.forEach(selectedId => {
          const currentStatus = currentAmbiguityIdToStateMap.get(selectedId);
          if (!currentStatus || currentStatus !== 'unanswered') {
            this.selectedForAgent.delete(selectedId);
          }
        });

        this.refreshFilteredListIfOpen(); // Refresh UI lists if they are open

        // console.log(`[ngOnInit sub REVISED END] Finished processing emission. ${testAmbiguityId} final state in this.ambiguities:`, ambigForTestIdAfterProcessing ? JSON.parse(JSON.stringify(ambigForTestIdAfterProcessing)) : 'NOT FOUND');
      })
    );
  }

// Ensure ngOnDestroy also resets flag and dismisses
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.checkedAmbiguityIds.clear();
    this.stopAgentSearch();
    this.destroy$.next();
    this.destroy$.complete();
    // console.log('[ngOnDestroy] Resetting navigation flag before dismissing all');
    this.isNavigatingInternally = false; // Ensure flag is false
    this.modalService.dismissAll('Component Destroyed');
  }

// --- Metodo Helper per chiudere modali precedenti ---
  private dismissPreviousModals(reason: string = 'Dismissing previous modal') {
    // Keep track of which modals were actually dismissed
    let dismissed = false;
    if (this.managerModalRef) {
      this.managerModalRef.dismiss(reason);
      this.managerModalRef = null;
      dismissed = true;
    }
    if (this.filteredModalRef) {
      this.filteredModalRef.dismiss(reason);
      this.filteredModalRef = null;
      dismissed = true;
    }
    if (this.detailsModalRef) {
      this.detailsModalRef.dismiss(reason);
      this.detailsModalRef = null;
      dismissed = true;
    }
    if (this.kbSelectorModalRef) {
      this.kbSelectorModalRef.dismiss(reason);
      this.kbSelectorModalRef = null;
      dismissed = true;
    }
    // Log if any modal was dismissed by this call
    // if (dismissed) {
    //   console.log(`[dismissPreviousModals] Dismissed previous modals with reason: ${reason}`);
    // }
  }

  // --- Metodo Helper per aggiornare la lista filtrata ---
  private refreshFilteredListIfOpen() {
    if (this.filteredModalRef && this.currentFilter) { // Controlla se il modale filtrato è aperto e un filtro è settato
      this.filteredAmbiguities = this.getAmbiguitiesByStateAndPriority(
        this.currentFilter.state,
        this.currentFilter.priority
      );
      // Potrebbe essere necessario forzare il change detection in casi rari
      // import { ChangeDetectorRef } from '@angular/core';
      // constructor(private cdRef: ChangeDetectorRef) {}
      // this.cdRef.detectChanges();
    } else {
      // console.log(`[refreshFilteredListIfOpen] Filtered list modal not open or filter not set. Skipping refresh.`);
    }
  }

  // Method to save the current session
  saveCurrentSession(customName?: string): void {
    // Show a loading indicator
    Swal.fire({
      title: 'Saving Session',
      text: 'Please wait while your session is being saved...',
      icon: 'info',
      showConfirmButton: false,
      allowOutsideClick: false
    });

    // Get the filename from StatoSessioneService
    this.sessioneService.fileName$.pipe(
      take(1),
      switchMap(fileName => {
        // Use filename as part of the session name if no custom name is provided
        const sessionName = customName || `Ambiguities for ${fileName || 'Project'}`;

        // Call the saveSessionOnDemand method from the SessionPersistenceService
        return this.sessionPersistenceService.saveSessionOnDemand(sessionName);
      }),
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Error saving session:', error);
        Swal.fire({
          title: 'Error',
          text: 'Failed to save the session: ' + (error.message || 'Unknown error'),
          icon: 'error'
        });
        throw error;
      })
    ).subscribe({
      next: (response) => {
        // console.log('Session saved successfully:', response);
        // Show success message
        Swal.fire({
          title: 'Success',
          text: 'Your session has been saved successfully!',
          icon: 'success',
          timer: 2000
        });
      },
      error: (error) => {
        console.error('Error in session save subscription:', error);
      }
    });
  }

  // Method to load knowledge base items
  loadKnowledgeBaseItems(): void {
    // Skip if already loading or loaded
    if (this.isLoadingKnowledgeBase || this.knowledgeBaseLoaded) {
      return;
    }

    this.isLoadingKnowledgeBase = true;
    this.knowledgeBaseError = null;

    // console.log('[loadKnowledgeBaseItems] Loading KB items...');

    combineLatest([
      this.authService.email$.pipe(take(1)),
      this.sessioneService.projectId$.pipe(take(1))
    ]).pipe(
      switchMap(([email, projectId]) => {
        return this.userStoryService.getKnowledgeBaseItems(email, projectId);
      }),
      map(items => {
        // Remove duplicates based on kb_id
        const uniqueMap = new Map();
        items.forEach(item => {
          // If we haven't seen this kb_id or the item is newer than what we have
          if (!uniqueMap.has(item.kb_id) ||
            new Date(uniqueMap.get(item.kb_id).lastModified) < new Date(item.lastModified)) {
            uniqueMap.set(item.kb_id, item);
          }
        });
        return Array.from(uniqueMap.values());
      }),
      takeUntil(this.destroy$),
      catchError(err => {
        this.knowledgeBaseError = 'Failed to load knowledge base items. Please try again.';
        this.isLoadingKnowledgeBase = false;
        console.error('Error loading knowledge base items:', err);
        return [];
      })
    ).subscribe({
      next: (items) => {
        // console.log(`[loadKnowledgeBaseItems] Loaded ${items.length} KB items`);
        this.knowledgeBaseItems = items;
        this.isLoadingKnowledgeBase = false;
        this.knowledgeBaseLoaded = true; // Mark as loaded
      },
      error: (err) => {
        console.error('Error loading knowledge base items:', err);
        this.isLoadingKnowledgeBase = false;
        this.knowledgeBaseError = 'Failed to load knowledge base items. Please try again.';
      }
    });
  }

  // Method to determine the type based on ID prefix (backward compat for T-/W- IDs)
  determineAmbiguityType(id: string): string {
    if (id?.startsWith('T-')) return 'technical';
    if (id?.startsWith('W-')) return 'workflow';
    if (id?.startsWith('A-')) return 'unified';
    return 'workflow';
  }

  // Get effective priority for an ambiguity (backward compat: old ambiguities default to medium)
  getEffectivePriority(ambiguity: Ambiguity): 'high' | 'medium' | 'low' {
    return ambiguity.priority || 'medium';
  }

  // Method to open the ambiguities manager modal
  openAmbiguitiesManager() {
    // *** No flag needed here, as we are not dismissing TO open this one ***
    // Although, if called from another modal, the calling function should handle the flag.
    // Let's assume this is the entry point or called after explicit closure.

    // We might dismiss *stale* modals if refs exist, but this isn't navigation *from* the manager
    this.dismissPreviousModals('Opening Manager');

    const modalOptions: NgbModalOptions = {
      size: 'lg',
      scrollable: true,
      windowClass: 'fixed-size-modal',
      beforeDismiss: (): Promise<boolean> | boolean => {
        // *** MODIFIED beforeDismiss LOGIC ***
        if (this.isNavigatingInternally) {
          // console.log('[Manager beforeDismiss] Allowing dismiss (internal navigation)');
          // IMPORTANT: Reset the flag *after* allowing dismissal but before the promise resolves if async
          // However, since this is synchronous return true, it's okay here.
          // If Swal was used, reset would need careful placement.
          // Let's reset it in the calling function for safety.
          return true; // Allow closing immediately
        }

        // Standard check when NOT navigating internally
        if (this.hasAnsweredAmbiguities()) {
          // console.log('[Manager beforeDismiss] Detected answered ambiguities. Showing confirmation.');
          return Swal.fire({
            title: 'Unprocessed Ambiguities',
            text: 'You have answered ambiguities that haven\'t been submitted yet. Are you sure you want to close?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, Close Anyway',
            cancelButtonText: 'No, Keep Open',
            reverseButtons: true,
            focusCancel: true
          }).then((result) => {
            if (result.isConfirmed) {
              // console.log('[Manager beforeDismiss] User confirmed closing.');
              return true; // Allow closing
            } else {
              // console.log('[Manager beforeDismiss] User cancelled closing.');
              return false; // Prevent closing
            }
          });
        } else {
          // console.log('[Manager beforeDismiss] No answered ambiguities detected. Allowing close.');
          return true; // Allow closing
        }
      }
    };

    this.managerModalRef = this.modalService.open(this.ambiguitiesManagerModal, modalOptions);

    this.managerModalRef.result.then(
      (result) => {
        // console.log(`[Ambiguities Manager] Modal closed with result: ${result}`);
        },
      (reason) => {
        // console.log(`[Ambiguities Manager] Modal dismissed with reason: ${reason}`);
      }
    ).finally(() => {
      this.managerModalRef = null;
      // Ensure flag is false if modal is closed/dismissed externally
      this.isNavigatingInternally = false;
      // console.log('[Manager finally] Manager modal closed/dismissed. Flag reset.');
    });
  }


// Show ambiguities filtered by state and priority
  showFilteredAmbiguities(state: 'unanswered' | 'answered' | 'solved', priority: 'high' | 'medium' | 'low') {
    this.currentFilter = { state, priority };
    this.filteredAmbiguities = this.getAmbiguitiesByStateAndPriority(state, priority);
    const labels: Record<string, string> = { high: 'High Priority', medium: 'Medium Priority', low: 'Low Priority' };
    this.filterTitle = labels[priority] || priority;

    // console.log('[showFilteredAmbiguities] Setting navigation flag to true');
    this.isNavigatingInternally = true; // *** SET FLAG ***
    try {
      this.dismissPreviousModals('Navigating to Filtered List'); // Dismiss manager or other modals
      // Open the new modal
      this.filteredModalRef = this.modalService.open(this.filteredAmbiguitiesModal, {
        size: 'lg',
        scrollable: true,
        windowClass: 'fixed-size-modal'
      });
      this.filteredModalRef.result.finally(() => {
        this.filteredModalRef = null;
        // Reset flag if this modal closes? No, reset happens below or in backToManager
      });
    } finally {
      // Use setTimeout to ensure the flag is reset *after* the current execution context,
      // allowing the dismissal process (including beforeDismiss) to complete first.
      setTimeout(() => {
        // console.log('[showFilteredAmbiguities] Resetting navigation flag (timeout)');
        this.isNavigatingInternally = false; // *** RESET FLAG ***
      }, 0);
    }
  }

// Navigate back to the manager
  backToManager() {
    // console.log('[backToManager] Setting navigation flag to true');
    this.isNavigatingInternally = true; // *** SET FLAG ***
    try {
      this.dismissPreviousModals('Navigating back to Manager'); // Chiudi modale filtrato
    } finally {
      // Reset flag *before* opening the manager, as opening manager doesn't need the flag.
      // console.log('[backToManager] Resetting navigation flag');
      this.isNavigatingInternally = false; // *** RESET FLAG ***
    }
    this.openAmbiguitiesManager(); // Riapri il manager (this might dismiss again, but flag is false now)
  }

// Navigate back to filtered list from ambiguity details
  backToFiltered() {
    // console.log('[backToFiltered] Setting navigation flag to true');
    this.isNavigatingInternally = true; // *** SET FLAG ***
    try {
      this.dismissPreviousModals('Navigating back to Filtered List'); // Chiudi dettagli o KB selector
    } finally {
      // Reset flag *before* calling showFilteredAmbiguities
      // console.log('[backToFiltered] Resetting navigation flag');
      this.isNavigatingInternally = false; // *** RESET FLAG ***
    }
    // Riapri usando i filtri correnti
    // showFilteredAmbiguities will handle its own flag setting/resetting for its dismissals
    this.showFilteredAmbiguities(this.currentFilter.state, this.currentFilter.priority);
  }

  getAmbiguityIndex(): number {
    if (!this.selectedAmbiguity) return -1;
    return this.filteredAmbiguities.findIndex(a => a.id === this.selectedAmbiguity.id);
  }

  navigateAmbiguity(dir: 1 | -1): void {
    const idx = this.getAmbiguityIndex();
    const next = this.filteredAmbiguities[idx + dir];
    if (!next) return;
    this.originalSelectedAmbiguity = JSON.parse(JSON.stringify(next));
    this.selectedAmbiguity = { ...this.originalSelectedAmbiguity };
    this.initialSelectedAmbiguityStateForReviewButton = this.originalSelectedAmbiguity.stato;
  }

  // Method to open a specific ambiguity for detailed view
  openAmbiguityDetails(ambiguity: Ambiguity) {
    this.originalSelectedAmbiguity = JSON.parse(JSON.stringify(ambiguity));
    this.selectedAmbiguity = { ...this.originalSelectedAmbiguity };

    // Store the state of the ambiguity as it was when this modal was opened
    this.initialSelectedAmbiguityStateForReviewButton = this.originalSelectedAmbiguity.stato; // << SET INITIAL STATE
    // console.log(`[openAmbiguityDetails] Initial state for review button: ${this.initialSelectedAmbiguityStateForReviewButton}`);


    // console.log('[openAmbiguityDetails] Setting navigation flag to true');
    this.isNavigatingInternally = true;
    try {
      this.dismissPreviousModals('Navigating to Details');
      this.detailsModalRef = this.modalService.open(this.ambiguityDetailsModal, {
        size: 'lg',
        scrollable: true,
        windowClass: 'fixed-size-modal',
      });

      this.detailsModalRef.result.then(
        (result) => {
          // console.log('Details modal closed with result:', result);
          // Pass true if the primary action (like 'Save') was taken
          this.handleDetailModalClose(result === 'Save');
        },
        (reason) => {
          // console.log('Details modal dismissed with reason:', reason);
          // Pass false if dismissed (e.g., 'Back', ESC, backdrop click)
          this.handleDetailModalClose(false);
        }
      ).finally(() => {
        this.detailsModalRef = null;
        // The reset of initialSelectedAmbiguityStateForReviewButton is now handled in handleDetailModalClose
        // console.log('[Details Modal finally] Flag reset is handled by handleDetailModalClose -> backToFiltered');
      });

    } finally {
      setTimeout(() => {
        // console.log('[openAmbiguityDetails] Resetting navigation flag (timeout)');
        this.isNavigatingInternally = false;
      }, 0);
    }
  }

  // --- Gestisce la chiusura del modale dettagli ---
  private handleDetailModalClose(wasSavedOrConfirmed: boolean) {
    // Prendi l'ambiguità potenzialmente modificata
    const potentiallyModifiedAmbiguity = this.selectedAmbiguity;
    const originalAmbiguity = this.originalSelectedAmbiguity;

    if (!originalAmbiguity) {
      console.error('[handleDetailModalClose] Original ambiguity reference lost!');
      this.initialSelectedAmbiguityStateForReviewButton = null; // << RESET STATE
      this.backToFiltered(); // Torna alla lista come fallback (backToFiltered handles its own flags)
      return;
    }

    // Determina se ci sono state modifiche effettive
    const wasModified = !isEqual(originalAmbiguity, potentiallyModifiedAmbiguity);

    // console.log(`[handleDetailModalClose] Ambiguity ${originalAmbiguity.id} - Saved/Confirmed: ${wasSavedOrConfirmed}, WasModified: ${wasModified}`);

    if (wasSavedOrConfirmed && wasModified) {
      // Se l'utente ha salvato E ci sono state modifiche
      // console.log(`   Updating ambiguity ${originalAmbiguity.id} in service.`);
      this.updateAmbiguityInList(potentiallyModifiedAmbiguity); // Aggiorna nel servizio

      // Se è stata modificata E rimane 'answered', rimuovi il check
      if (potentiallyModifiedAmbiguity.stato === 'answered') {
        // console.log(`   Modified and still answered. Removing check.`);
        this.checkedAmbiguityIds.delete(originalAmbiguity.id);
      }
    } else if (wasModified && !wasSavedOrConfirmed) {
      // Se ci sono state modifiche MA l'utente ha annullato/chiuso
      // console.log(`   Modified but not saved. Reverting changes (implicit by not calling update).`);
      // Non fare nulla, le modifiche a this.selectedAmbiguity sono temporanee.
    } else {
      // Nessuna modifica o non salvato senza modifiche
      // console.log(`   No relevant changes or not saved.`);
    }

    // Se l'ambiguità (originale o modificata) NON è 'answered', assicurati che non sia nel set checked
    if (potentiallyModifiedAmbiguity.stato !== 'answered') {
      if (this.checkedAmbiguityIds.has(originalAmbiguity.id)) {
        // console.log(`   State is not 'answered'. Ensuring check is removed.`);
        this.checkedAmbiguityIds.delete(originalAmbiguity.id);
      }
    }

// Pulisci la reference all'originale e lo stato iniziale per il bottone
    this.originalSelectedAmbiguity = null;
    this.initialSelectedAmbiguityStateForReviewButton = null; // << RESET STATE HERE
    // console.log('[handleDetailModalClose] Reset initialSelectedAmbiguityStateForReviewButton.');

    // console.log('[handleDetailModalClose] Navigating back to filtered list.');
    this.backToFiltered();
  }

  // --- Metodo toggleAmbiguityChecked (INVARIATO) ---
  toggleAmbiguityChecked(ambiguity: Ambiguity, event?: MouseEvent): void { // Aggiunto '?' per rendere event opzionale
    // Chiama stopPropagation solo se l'evento è stato passato (necessario nella lista)
    if (event) {
      event.stopPropagation();
    }

    // Assicurati che l'ambiguità esista prima di procedere
    if (!ambiguity || !ambiguity.id) {
      console.warn('[toggleAmbiguityChecked] Invalid ambiguity object received.');
      return;
    }

    const id = ambiguity.id;
    if (this.checkedAmbiguityIds.has(id)) {
      this.checkedAmbiguityIds.delete(id);
      // console.log(`[toggleAmbiguityChecked] Removed ${id} from checked set.`);
    } else {
      // Aggiungi solo se l'ambiguità è effettivamente 'answered'
      // Questo previene l'aggiunta accidentale se lo stato cambia rapidamente
      if (ambiguity.stato === 'answered') {
        this.checkedAmbiguityIds.add(id);
        // console.log(`[toggleAmbiguityChecked] Added ${id} to checked set.`);
      } else {
        console.warn(`[toggleAmbiguityChecked] Attempted to check ambiguity ${id} which is not in 'answered' state (${ambiguity.stato}). Ignoring.`);
      }
    }

    // Forza il rilevamento delle modifiche se necessario (di solito non serve con Set/OnPush)
    // import { ChangeDetectorRef } from '@angular/core';
    // constructor(private cdRef: ChangeDetectorRef) {}
    // this.cdRef.detectChanges();
  }

  // --- Metodo isAmbiguityChecked ---
  isAmbiguityChecked(ambiguity: Ambiguity): boolean {
    return this.checkedAmbiguityIds.has(ambiguity.id);
  }

  // Open knowledge base selector modal - UPDATED to not close details modal
  openKnowledgeBaseSelector() {
    if (!this.knowledgeBaseLoaded) {
      this.loadKnowledgeBaseItems();
    }
    this.filterKnowledgeBaseItemsByType(this.selectedAmbiguity?.type || 'technical');
    this.searchTerm = '';

    // console.log('[openKnowledgeBaseSelector] Setting navigation flag to true');
    this.isNavigatingInternally = true; // *** SET FLAG ***
    try {
      // Dismiss the details modal specifically
      if (this.detailsModalRef) {
        this.detailsModalRef.dismiss('Navigating to KB Selector');
        this.detailsModalRef = null;
      } else {
        // Or dismiss all if details ref is lost? Safer to dismiss all.
        this.dismissPreviousModals('Navigating to KB Selector');
      }

      // Use setTimeout to ensure the previous modal is fully closed *and* flag is reset before opening new one
      setTimeout(() => {
        // console.log('[openKnowledgeBaseSelector] Opening KB Selector Modal (timeout)');
        this.kbSelectorModalRef = this.modalService.open(this.knowledgeBaseModalSelector, {
          size: 'lg',
          scrollable: true,
          windowClass: 'fixed-size-modal',
          backdrop: 'static',
          ariaLabelledBy: 'kb-selector-title',
          keyboard: false
        });
        this.kbSelectorModalRef.result.then(
          (result) => { /* Closed */ },
          (reason) => { /* Dismissed (Done/Cancel) */ }
        ).finally(() => {
          this.kbSelectorModalRef = null;
          // Flag reset handled by finish/cancel methods
        });

        // Reset the flag *after* initiating the opening of the new modal
        // console.log('[openKnowledgeBaseSelector] Resetting navigation flag (timeout)');
        this.isNavigatingInternally = false; // *** RESET FLAG ***

      }, 50); // Small delay often helps with modal transitions

    } catch (e) {
      // Ensure flag is reset even if error occurs during dismissal/opening
      console.error('[openKnowledgeBaseSelector] Error during navigation:', e);
      // console.log('[openKnowledgeBaseSelector] Resetting navigation flag (error catch)');
      this.isNavigatingInternally = false; // *** RESET FLAG ***
    }
  }

  // Method to finalize KB selection and close the modal
  finishKbSelection(): void {
    // Make sure to update the ambiguity in the list if needed (e.g., state change)
    // The selection itself should have already called updateAmbiguityInList
    // But we might re-evaluate state here just in case.
    this.updateAmbiguityState(this.selectedAmbiguity); // Ensure state is correct
    this.updateAmbiguityInList(this.selectedAmbiguity); // Persist any final state change

    // Show a success message (optional)
    if (this.selectedAmbiguity.linkedKbItems && this.selectedAmbiguity.linkedKbItems.length > 0) {
      // Consider if this Swal is needed or if toasts during selection are enough
      // Swal.fire({
      //   title: 'Knowledge Base Items Linked',
      //   text: `Successfully linked ${this.selectedAmbiguity.linkedKbItems.length} items. Returning to details.`,
      //   icon: 'success',
      //   timer: 2000
      // });
      // console.log(`[finishKbSelection] Finished linking ${this.selectedAmbiguity.linkedKbItems.length} KB items.`);
    }

    // console.log('[finishKbSelection] Setting navigation flag to true');
    this.isNavigatingInternally = true; // *** SET FLAG ***
    try {
      // Dismiss KB selector
      if (this.kbSelectorModalRef) {
        // console.log('[finishKbSelection] Dismissing KB Selector modal.');
        this.kbSelectorModalRef.dismiss('Finished KB Selection');
        this.kbSelectorModalRef = null;
      } else {
        console.warn('[finishKbSelection] KB Selector modal reference not found, dismissing all as fallback.');
        this.dismissPreviousModals('Finished KB Selection'); // Fallback
      }
    } finally {
      // Reset flag *before* opening details
      // console.log('[finishKbSelection] Resetting navigation flag');
      this.isNavigatingInternally = false; // *** RESET FLAG ***
    }

    // Reopen the ambiguity details modal
    // openAmbiguityDetails will handle its own flag logic
    // console.log('[finishKbSelection] Reopening Ambiguity Details modal.');
    this.openAmbiguityDetails(this.selectedAmbiguity);
  }

  // Method to cancel KB selection and restore original state
  cancelKbSelection(): void {
    const closeKbAndReopenDetails = () => {
      // console.log('[cancelKbSelection] Setting navigation flag to true');
      this.isNavigatingInternally = true; // *** SET FLAG ***
      try {
        // Dismiss KB selector
        if (this.kbSelectorModalRef) {
          this.kbSelectorModalRef.dismiss('Cancelled KB Selection');
          this.kbSelectorModalRef = null;
        } else {
          this.dismissPreviousModals('Cancelled KB Selection'); // Fallback
        }
      } finally {
        // Reset flag *before* opening details
        // console.log('[cancelKbSelection] Resetting navigation flag');
        this.isNavigatingInternally = false; // *** RESET FLAG ***
      }

      // Reopen the ambiguity details modal
      this.openAmbiguityDetails(this.selectedAmbiguity);
    };

    if (this.selectedAmbiguity.linkedKbItems && this.selectedAmbiguity.linkedKbItems.length > 0) {
      Swal.fire({
        title: 'Cancel Selection?',
        // text: 'Are you sure you want to cancel? The items you selected will still be saved.', // Kept original text
        text: 'Are you sure you want to go back? Changes to linked items in this session will be kept.', // Alternative text
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, go back',
        cancelButtonText: 'No, continue selecting'
      }).then((result) => {
        if (result.isConfirmed) {
          closeKbAndReopenDetails();
        }
        // else: Do nothing, stay in KB selector
      });
    } else {
      // No items added, just close and reopen
      closeKbAndReopenDetails();
    }
  }

  // Filter knowledge base items by type
  filterKnowledgeBaseItemsByType(type: string): void {
    // Assuming knowledge base items have a 'type' field that matches the ambiguity type
    this.filteredKnowledgeBaseItems = this.knowledgeBaseItems.filter(item =>
      item.type === type || !item.type // Include items with no specified type
    );
  }

  // Helper method to check if a knowledge base item is already linked to the current ambiguity
  isItemAlreadyLinked(itemId: string): boolean {
    if (!this.selectedAmbiguity || !this.selectedAmbiguity.linkedKbItems) {
      return false;
    }

    return this.selectedAmbiguity.linkedKbItems.some(item => item.kbItemId === itemId);
  }

  // Search knowledge base items
  searchKnowledgeBaseItems(): void {
    if (!this.searchTerm.trim()) {
      this.filterKnowledgeBaseItemsByType(this.selectedAmbiguity?.type || 'technical');
      return;
    }

    const term = this.searchTerm.toLowerCase().trim();
    this.filteredKnowledgeBaseItems = this.knowledgeBaseItems.filter(item =>
      (item.title.toLowerCase().includes(term) ||
        item.term.toLowerCase().includes(term) ||
        item.definition.toLowerCase().includes(term)) &&
      (item.type === this.selectedAmbiguity?.type || !item.type)
    );
  }

  // Select a knowledge base item to link to the current ambiguity - UPDATED for multiple links
  selectKnowledgeBaseItem(item: KnowledgeBaseItem): void {
    // Create a deep copy of the current ambiguity to avoid reference issues
    const updatedAmbiguity: Ambiguity = { ...this.selectedAmbiguity };

    // Initialize linkedKbItems array if it doesn't exist
    if (!updatedAmbiguity.linkedKbItems) {
      updatedAmbiguity.linkedKbItems = [];
    }

    // Check if this item is already linked
    const isAlreadyLinked = this.isItemAlreadyLinked(item._id);

    if (isAlreadyLinked) {
      // Show a message that the item is already linked
      Swal.fire({
        title: 'Already Selected',
        text: 'This knowledge base item is already linked to this ambiguity.',
        icon: 'info',
        timer: 2000,
        position: 'top-end',
        toast: true,
        showConfirmButton: false
      });
      return;
    }

    // Create a new linked KB item
    const newLinkedItem: LinkedKnowledgeBaseItem = {
      kbItemId: item._id,
      title: item.title,
      term: item.term,
      definition: item.definition,
      examples: item.examples
    };

    // Add to the linkedKbItems array
    updatedAmbiguity.linkedKbItems.push(newLinkedItem);

    // Mark the ambiguity as answered
    updatedAmbiguity.stato = 'answered';

    // Update the local reference
    this.selectedAmbiguity = updatedAmbiguity;

    // Update the ambiguity in the list
    this.updateAmbiguityInList(updatedAmbiguity);

    // Show a confirmation toast
    Swal.fire({
      title: 'Item Added',
      text: `"${item.title}" has been linked to this ambiguity.`,
      icon: 'success',
      timer: 1500,
      position: 'top-end',
      toast: true,
      showConfirmButton: false
    });

    // IMPORTANT: Don't close the KB selector modal
  }

  // Remove linked knowledge base item - UPDATED to remove specific item
  removeKnowledgeBaseLink(kbItemId: string): void {
    if (!this.selectedAmbiguity.linkedKbItems || this.selectedAmbiguity.linkedKbItems.length === 0) {
      return;
    }

    // Ask for confirmation
    Swal.fire({
      title: 'Remove Link?',
      text: 'Are you sure you want to remove this knowledge base item link?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, remove it',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        // Create a copy of the ambiguity
        const updatedAmbiguity = { ...this.selectedAmbiguity };

        // Remove the specified KB item
        updatedAmbiguity.linkedKbItems = updatedAmbiguity.linkedKbItems.filter(
          item => item.kbItemId !== kbItemId
        );

        // If there are no more linked KB items AND no resolution text, mark as unanswered
        if (updatedAmbiguity.linkedKbItems.length === 0 && (!updatedAmbiguity.resolution || !updatedAmbiguity.resolution.trim())) {
          updatedAmbiguity.stato = 'unanswered';
        }

        // Update the selected ambiguity
        this.selectedAmbiguity = updatedAmbiguity;

        // Update the ambiguity in the list
        this.updateAmbiguityInList(updatedAmbiguity);
      }
    });
  }

  // Helper method to check if an ambiguity has linked KB items
  hasLinkedKbItems(ambiguity: Ambiguity): boolean {
    return ambiguity.linkedKbItems && ambiguity.linkedKbItems.length > 0;
  }

  // Helper method to get the count of linked KB items
  getLinkedKbItemsCount(ambiguity: Ambiguity): number {
    return ambiguity.linkedKbItems ? ambiguity.linkedKbItems.length : 0;
  }

// --- Metodo updateAmbiguityInList (Aggiorna il servizio e gestisce pulizia check) ---
  updateAmbiguityInList(updatedAmbiguity: Ambiguity) {
    // Ensure the updatedAmbiguity object itself has the final correct state before updating the list.
    // This should already be true if calling functions (handleDetailModalClose, selectKnowledgeBaseItem)
    // have correctly set updatedAmbiguity.stato based on its content (resolution, links).
    // console.log(`[updateAmbiguityInList REVISED] Updating ${updatedAmbiguity.id} with state: <span class="math-inline">\{updatedAmbiguity\.stato\}, resolution\: "</span>{updatedAmbiguity.resolution}", linkedItems: ${updatedAmbiguity.linkedKbItems?.length || 0}`);

    const allAmbiguities = [...this.sessioneService.getAmbiguities()];
    const index = allAmbiguities.findIndex(a => a.id === updatedAmbiguity.id);

    if (index !== -1) {
      // The updatedAmbiguity from the modal (e.g., this.selectedAmbiguity) should have the correct state
      // calculated by updateAmbiguityState or selectKnowledgeBaseItem.
      const updatedCopy: Ambiguity = JSON.parse(JSON.stringify(updatedAmbiguity));
      allAmbiguities[index] = updatedCopy;

      // No need to re-evaluate or clean checkedAmbiguityIds here,
      // the ngOnInit subscription will do it based on the new truth from the service.

      // console.log(`   [updateAmbiguityInList REVISED] Calling sessioneService.updateAmbiguities with updated list.`);
      this.sessioneService.updateAmbiguities(allAmbiguities);
    } else {
      console.warn(`[updateAmbiguityInList REVISED] Ambiguity ${updatedAmbiguity.id} NOT FOUND in service list!`);
    }
  }

// --- Metodo updateAmbiguityState (gestisce cambio stato live e pulizia check) ---
  updateAmbiguityState(ambiguity: Ambiguity) {
    // Lavora sulla copia in this.selectedAmbiguity se siamo nei dettagli
    const targetAmbiguity = this.detailsModalRef ? this.selectedAmbiguity : ambiguity;

    if (!targetAmbiguity) return;

    const originalState = targetAmbiguity.stato; // Stato prima della modifica live

    if ((targetAmbiguity.resolution && targetAmbiguity.resolution.trim()) || this.hasLinkedKbItems(targetAmbiguity)) {
      targetAmbiguity.stato = 'answered';
    } else {
      targetAmbiguity.stato = 'unanswered';
      // Se diventa unanswered, rimuovi dal set checked
      if (originalState === 'answered') { // Solo se *prima* era answered
        this.checkedAmbiguityIds.delete(targetAmbiguity.id);
      }
    }
    // Non chiamare updateAmbiguityInList qui, verrà fatto in handleDetailModalClose se salvato
  }

  // Method to get ambiguities by state
  getAmbiguitiesByState(stato: 'unanswered' | 'answered' | 'solved'): Ambiguity[] {
    return this.ambiguities.filter(a => a.stato === stato);
  }

  // Method to get ambiguities by state and type (kept for backward compat)
  getAmbiguitiesByStateAndType(
    stato: 'unanswered' | 'answered' | 'solved',
    type: string
  ): Ambiguity[] {
    return this.ambiguities.filter(a => a.stato === stato && a.type === type);
  }

  // Method to get ambiguities by state and priority
  getAmbiguitiesByStateAndPriority(
    stato: 'unanswered' | 'answered' | 'solved',
    priority: 'high' | 'medium' | 'low'
  ): Ambiguity[] {
    return this.ambiguities.filter(a => a.stato === stato && this.getEffectivePriority(a) === priority);
  }

  // Get priority badge CSS class
  getPriorityBadgeClass(priority: string | undefined): string {
    switch (priority) {
      case 'high': return 'badge-priority-high';
      case 'medium': return 'badge-priority-medium';
      case 'low': return 'badge-priority-low';
      default: return 'badge-priority-medium';
    }
  }

  // Check if there are any answered ambiguities
  hasAnsweredAmbiguities(): boolean {
    return this.ambiguities.some(a => a.stato === 'answered');
  }

  // Get CSS class for state badge
  getStateBadgeClass(state: string): string {
    switch (state) {
      case 'unanswered': return 'bg-warning';
      case 'answered': return 'bg-primary';
      case 'solved': return 'bg-success';
      default: return 'bg-secondary';
    }
  }

  // Method to submit filtered resolutions
  submitFilteredResolutions() {
    // Get only the filtered ambiguities that are in 'answered' state
    const ambigsToSubmit = this.filteredAmbiguities.filter(a => a.stato === 'answered');

    if (ambigsToSubmit.length === 0) {
      Swal.fire({
        title: 'No Resolutions to Submit',
        text: 'There are no ambiguities marked as \'Answered\' in the current filtered view.',
        icon: 'info'
      });
      return;
    }
    // console.log(`Submitting ${ambigsToSubmit.length} filtered ambiguities.`);
    this.processAmbiguities(ambigsToSubmit);
  }

  // Method to submit all resolutions
  submitResolutions() {
    // Get all ambiguities that are in 'answered' state
    const ambigsToSubmit = this.ambiguities.filter(a => a.stato === 'answered');

    if (ambigsToSubmit.length === 0) {
      Swal.fire({
        title: 'No Resolutions to Submit',
        text: 'There are no ambiguities currently marked as \'Answered\'.',
        icon: 'info'
      });
      return;
    }
    // console.log(`Submitting all ${ambigsToSubmit.length} answered ambiguities.`);
    this.processAmbiguities(ambigsToSubmit);
  }

  private processAmbiguities(ambiguitiesToSubmit: Ambiguity[]) {
    // 1. Use a simple boolean flag to prevent concurrent executions.
    // This is more reliable and easier to reason about than checking a subscription object.
    if (this.isProcessing) {
      console.warn('Processing is already in progress. Please wait.');
      Swal.fire('In Progress', 'Your previous submission is still processing.', 'info');
      return;
    }

    // 2. Set the flag to true IMMEDIATELY after the guard.
    this.isProcessing = true;

    Swal.fire({
      title: 'Processing Ambiguities',
      text: 'Please wait, updating analysis based on your feedback...',
      icon: 'info',
      showConfirmButton: false,
      allowOutsideClick: false,
      didOpen: () => { Swal.showLoading(); }
    });

    this.sessioneService.resetDataDerivedFromAnalysis();

    // Get Necessary Data
    const coreAnalysisSession = this.sessioneService.getCoreAnalysis();
    if (!coreAnalysisSession || !coreAnalysisSession.scenario_analysis) {
      Swal.fire('Error', 'Core analysis data is missing or incomplete.', 'error');
      this.isProcessing = false; // <-- Reset flag on early exit
      return;
    }
    const analysisText = `Scenario Analysis:\n${coreAnalysisSession.scenario_analysis}`;

    // Build the observable chain
    forkJoin({
      userStory: this.sessioneService.refinedStoryString$.pipe(take(1)),
      nome_sessione: this.sessioneService.sessionName$.pipe(take(1)),
      email: this.authService.email$.pipe(take(1)),
      projectId: this.sessioneService.projectId$.pipe(take(1)),
      language: this.sessioneService.projectLanguage$.pipe(take(1))
    }).pipe(
      // Use switchMap for the entire processing pipeline
      switchMap(initialData => {
        const { userStory, nome_sessione, email, projectId, language } = initialData;
        if (!userStory || !nome_sessione || !email || !projectId || !language) {
          throw new Error('Missing required data for processing ambiguities.');
        }
        const ambiguitiesCopy = JSON.parse(JSON.stringify(ambiguitiesToSubmit));
        // G2: Attach agent_proposed_text so backend can detect unchanged acceptance (Caso A)
        ambiguitiesCopy.forEach((a: any) => {
          if (a.agentResult?.proposed_text) {
            a.agent_proposed_text = a.agentResult.proposed_text;
          }
        });
        // --- STEP 1: Process Ambiguities & Regenerate Core Analysis ---
        return this.userStoryService.processAmbiguities(
          nome_sessione, userStory, analysisText, language, projectId, email, ambiguitiesCopy
        ).pipe(
          // --- STEP 2: Extract Constraints ---
          switchMap(coreResponse => {
            const newCoreAnalysisText = coreResponse.core_analysis?.scenario_analysis;
            if (!newCoreAnalysisText) {
              throw new Error('Failed to get new core analysis text from the first step.');
            }
            this.sessioneService.coreAnalysisSubject.next(coreResponse.core_analysis);
            Swal.update({ text: 'Core analysis updated. Extracting constraints...' });
            return this.userStoryService.extractConstraints(this.sessioneService.getProjectId(), this.sessioneService.getSessionName(), newCoreAnalysisText, language).pipe(
              map(constraintsResponse => ({ coreResponse, constraintsResponse, userStory, language }))
            );
          }),
          // --- STEP 3: Generate Requirements ---
          switchMap(({ coreResponse, constraintsResponse, userStory, language }) => {
            if (constraintsResponse) {
              this.sessioneService.updateExtractedConstraints(constraintsResponse.constraints);
            }
            Swal.update({ text: 'Constraints extracted. Generating new requirements...' });
            return this.userStoryService.generateRequirementsAnalysis(
              nome_sessione, userStory, projectId, JSON.stringify(coreResponse.core_analysis), language
            ).pipe(
              map(requirementsResponse => ({ coreResponse, requirementsResponse }))
            );
          })
        );
      }),
      // 3. IMPORTANT: Use takeUntil(this.destroy$) to automatically cancel
      // this entire operation if the component is destroyed mid-flight.
      // This prevents memory leaks and zombie processes.
      takeUntil(this.destroy$)
    ).subscribe({
      next: ({ coreResponse, requirementsResponse }) => {
        try {
          // --- FINAL STEP: Handle Final Responses ---
          this.sessioneService.requirementsAnalysisSubject.next(requirementsResponse.requirements_analysis);

          const submittedIds = new Set(ambiguitiesToSubmit.map(a => a.id));
          let processedCount = 0;
          const updatedAmbiguitiesList = this.sessioneService.getAmbiguities().map(ambiguity => {
            if (submittedIds.has(ambiguity.id)) {
              processedCount++;
              const updatedAmbiguity = { ...ambiguity, stato: 'solved' as 'solved' };
              this.checkedAmbiguityIds.delete(updatedAmbiguity.id);
              // Optional: Merge backend KB mappings if needed
              const backendLinkedItems = coreResponse.kb_item_mappings?.[updatedAmbiguity.id];
              if (backendLinkedItems?.length > 0) {
                updatedAmbiguity.linkedKbItems = updatedAmbiguity.linkedKbItems || [];
                backendLinkedItems.forEach(backendItem => {
                  if (!updatedAmbiguity.linkedKbItems.some(existing => existing.kbItemId === backendItem.kbItemId)) {
                    updatedAmbiguity.linkedKbItems.push(backendItem);
                  }
                });
              }
              return updatedAmbiguity;
            }
            return ambiguity;
          });

          this.sessioneService.updateAmbiguities(updatedAmbiguitiesList);
          this.dismissPreviousModals('Processing Complete');

          Swal.fire({
            title: 'Processing Complete!',
            text: `${processedCount} ambiguities processed. All analyses have been updated. Session automatically saved.`,
            icon: 'success',
            timer: 4000,
            timerProgressBar: true
          }).then(() => {
            this.saveCurrentSession(`Processed ${processedCount} ambiguities`);
          });

        } catch (e) {
          console.error('Error during success handler logic:', e);
          Swal.fire('Internal Error', 'The process succeeded but failed to update the UI. Please check the console.', 'error');
        } finally {
          // 4. Use a finally block to GUARANTEE the flag is reset on success.
          this.isProcessing = false;
        }
      },
      error: (error) => {
        try {
          console.error('Error during ambiguity processing pipeline:', error);
          Swal.fire('Processing Error', 'An error occurred: ' + (error.message || 'Unknown API error.'), 'error');
        } finally {
          // 5. Also use a finally block to GUARANTEE the flag is reset on error.
          this.isProcessing = false;
        }
      }
    });
  }

  // Method to delete an ambiguity
  async deleteAmbiguity(ambiguityId: string) {
    const result = await Swal.fire({
      title: 'Delete Ambiguity?',
      text: `Are you sure you want to delete ambiguity ${ambiguityId}? This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, keep it'
    });

    if (result.isConfirmed) {
      // console.log(`[deleteAmbiguity] User confirmed deletion for ${ambiguityId}`);

      // Rimuovi dal set dei checked
      if (this.checkedAmbiguityIds.has(ambiguityId)) {
        this.checkedAmbiguityIds.delete(ambiguityId);
        // console.log(`[deleteAmbiguity] Removed ${ambiguityId} from checked set.`);
      }

      // Aggiorna il servizio
      const currentAmbiguities = this.sessioneService.getAmbiguities().filter(a => a.id !== ambiguityId);
      this.sessioneService.updateAmbiguities(currentAmbiguities); // This triggers ngOnInit update

      // Dismiss modals cleanly - Ensure flag is false first
      // console.log('[deleteAmbiguity] Resetting navigation flag before dismissing modals');
      this.isNavigatingInternally = false; // Make sure flag is false
      this.dismissPreviousModals('Ambiguity Deleted'); // Dismiss any open modal (details, filtered list etc.)

      Swal.fire({
        title: 'Deleted!',
        text: `Ambiguity ${ambiguityId} has been removed.`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      }).then(() => {
        // Riapri il manager solo se ci sono ancora ambiguità
        // Check the updated list (this.ambiguities should be updated by the service call trigger)
        if (this.ambiguities.length > 0) {
          // console.log('[deleteAmbiguity] Reopening manager modal.');
          // No flag needed here, openAmbiguitiesManager handles its own setup/dismissals
          this.openAmbiguitiesManager();
        } else {
          // console.log('[deleteAmbiguity] No ambiguities left, not reopening manager.');
          this.hasAmbiguities = false;
        }
      });
    } else {
      // console.log(`[deleteAmbiguity] User cancelled deletion for ${ambiguityId}`);
    }
  }


  // --- Agent selection methods ---

  // Pre-select all ambiguities of a given priority for the agent
  selectByPriority(priority: 'high' | 'medium' | 'low'): void {
    const matching = this.ambiguities.filter(
      a => a.stato === 'unanswered' && this.getEffectivePriority(a) === priority
    );
    matching.forEach(a => this.selectedForAgent.add(a.id));
  }

  toggleAgentSelection(id: string): void {
    if (this.selectedForAgent.has(id)) {
      this.selectedForAgent.delete(id);
    } else {
      this.selectedForAgent.add(id);
    }
  }

  isSelectedForAgent(id: string): boolean {
    return this.selectedForAgent.has(id);
  }

  getAgentSelectionCount(): number {
    return this.selectedForAgent.size;
  }

  getAgentSelectedAmbiguities(): Ambiguity[] {
    const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
    return this.ambiguities
      .filter(a => this.selectedForAgent.has(a.id))
      .sort((a, b) => (priorityOrder[this.getEffectivePriority(a)] || 1) - (priorityOrder[this.getEffectivePriority(b)] || 1));
  }

  openAgentSelectionModal(): void {
    // If nothing selected yet, pre-select all high priority
    if (this.selectedForAgent.size === 0) {
      this.selectByPriority('high');
    }
    this.modalService.open(this.agentSelectionModal, { size: 'lg', scrollable: true });
  }

  clearAgentSelection(): void {
    this.selectedForAgent.clear();
  }

  // Launch the KB agent on selected ambiguities — parallel pool (max 5 concurrent)
  findAutomaticKbMatches(): void {
    const toProcess = this.getAgentSelectedAmbiguities();

    if (toProcess.length === 0) {
      Swal.fire({
        title: 'No Ambiguities Selected',
        text: 'Select ambiguities to analyze before running the KB agent.',
        icon: 'info'
      });
      return;
    }

    // Start progress tracking
    this.agentSearching = true;
    this.agentProgress = { current: 0, total: toProcess.length };
    let resolvedCount = 0;

    this.sessioneService.projectId$.pipe(take(1)).subscribe(projectId => {
      const story = this.sessioneService.getUserStory() || '';

      // Create one observable per ambiguity — take(1) ensures completion so mergeMap frees the slot
      const calls = toProcess.map(ambiguity =>
        this.userStoryService.agentKbResolveSingle(projectId, ambiguity, story).pipe(
          take(1),
          catchError(err => {
            console.error(`KB agent error for ${ambiguity.id}:`, err);
            return of(null); // Don't break the pool on individual errors
          })
        )
      );

      // Run with mergeMap concurrency limit = 5
      this.agentSearchSubscription = from(calls).pipe(
        mergeMap(call => call, 5),
        finalize(() => {
          this.agentSearching = false;
          this.agentSearchSubscription = null;
        })
      ).subscribe({
        next: (response) => {
          if (!response) {
            this.agentProgress.current++;
            return;
          }

          // Merge result into the ambiguity
          const allAmbiguities = [...this.sessioneService.getAmbiguities()];
          const result = response.result;
          const index = allAmbiguities.findIndex(a => a.id === result.ambiguity_id);

          if (index !== -1) {
            const fromKb = result.sources?.some(s => !!s.kb_item_id) ?? false;
            allAmbiguities[index] = {
              ...allAmbiguities[index],
              agentResult: { ...result, agent_proposal_from_kb: fromKb },
              agent_proposal_from_kb: fromKb,
              agent_proposed_text: result.proposed_text ?? undefined,
              resolution: result.proposed_text ?? allAmbiguities[index].resolution,
              stato: result.proposed_text ? 'answered' : allAmbiguities[index].stato,
            };
            if (result.proposed_text) resolvedCount++;
            this.sessioneService.updateAmbiguities(allAmbiguities);
          }

          this.agentProgress.current++;

          // Autosave after each result
          this.sessionPersistenceService.saveSessionOnDemand().pipe(take(1)).subscribe();
        },
        complete: () => {
          if (resolvedCount === 0) {
            Swal.fire({ title: 'No Resolutions Found', text: 'The KB agent could not find answers for any ambiguity.', icon: 'info' });
          } else {
            Swal.fire({ title: 'KB Agent Complete', text: `Found proposed resolutions for ${resolvedCount} out of ${toProcess.length} ambiguities.`, icon: 'success' });
          }
        },
        error: (error) => {
          console.error('Agent KB pool error:', error);
          Swal.fire({ title: 'Error', text: 'KB agent search failed.', icon: 'error' });
        }
      });
    });
  }

  stopAgentSearch(): void {
    if (this.agentSearchSubscription) {
      this.agentSearchSubscription.unsubscribe();
      this.agentSearchSubscription = null;
    }
    this.agentSearching = false;
  }

  // --- Agent debug helpers ---

  hasAgentResult(ambiguity: Ambiguity): boolean {
    return !!ambiguity?.agentResult?.proposed_text;
  }

  getConfidenceBadgeClass(confidence: string | null): string {
    switch (confidence) {
      case 'high': return 'badge-confidence-high';
      case 'medium': return 'badge-confidence-medium';
      case 'low': return 'badge-confidence-low';
      default: return 'badge-confidence-low';
    }
  }

  openAgentDebugModal(ambiguity: Ambiguity): void {
    if (ambiguity?.agentResult?.debug) {
      this.activeAgentDebug = ambiguity.agentResult.debug;
      this.agentDebugCopied = false;
      const options: NgbModalOptions = { size: 'xl', scrollable: true };
      this.modalService.open(this.agentDebugModal, options);
    }
  }

  copyAgentDebug(): void {
    if (this.activeAgentDebug) {
      navigator.clipboard.writeText(JSON.stringify(this.activeAgentDebug, null, 2));
      this.agentDebugCopied = true;
      setTimeout(() => this.agentDebugCopied = false, 2000);
    }
  }

  clearAgentResolution(ambiguity: Ambiguity): void {
    const proposalText = ambiguity.agentResult?.proposed_text ?? '';
    const resolutionUnchanged = (ambiguity.resolution ?? '').trim() === proposalText.trim();
    ambiguity.agentResult = undefined;
    ambiguity.agent_proposal_from_kb = undefined;
    if (resolutionUnchanged) {
      ambiguity.resolution = '';
    }
    this.updateAmbiguityState(ambiguity);
    this.updateAmbiguityInList(ambiguity);
  }

  // G7: Returns null if no agent involvement, false if agent response unchanged, true if modified
  isResolutionModified(ambiguity: Ambiguity): boolean | null {
    if (!ambiguity?.agentResult?.proposed_text || !ambiguity?.resolution) return null;
    return ambiguity.resolution.trim() !== ambiguity.agentResult.proposed_text.trim();
  }
}
